/**
 * Turning what a user typed into what we should store.
 *
 * Vocabulary comes from the tagging service (openapi.json, Tagging Service
 * 0.1.0). Two enums do the work:
 *
 *   TagCategory   what the tag IS — company, ticker, index, fx_pair, person,
 *                 sector, term, …
 *   LabelCategory what a given label OF that tag is — RIC ("AAPL.O"),
 *                 ISIN, canonical, adjectival, normal
 *
 * That second one is why the plain ticker is *read*, never derived: "AAPL" and
 * "AAPL.O" are both labels on the Apple tag, distinguished by category. Naive
 * dot-stripping would break BRK.B and need a hand-maintained suffix list.
 */

/** TagResponse.category — what the tag is. */
export type TagCategory =
  | "asset_class" | "calendar" | "category" | "classification" | "commodity"
  | "company" | "country" | "currency" | "economic_data" | "exchange"
  | "fx_pair" | "geographical" | "index" | "organisation" | "person"
  | "region" | "sector" | "session" | "term" | "ticker";

/**
 * TagLabelResponse.category — what this particular label is.
 *
 * VERIFIED ABSENT (2026-08-18): neither `/tags/search/{q}` nor `/tags/ids/{ids}`
 * returns this field. It is kept because the design above is the right one and
 * costs nothing if the service starts sending it, but nothing may *depend* on
 * it — see `classification`, which is what the service actually sends.
 */
export type LabelCategory = "RIC" | "ISIN" | "adjectival" | "canonical" | "normal";

/**
 * TagLabelResponse.classification — the field the service really sends.
 * Observed values: "abbreviation" (AAPL, AAPL.OQ, .SPX, and ISINs) and
 * "phrase" (Apple Inc, S&P 500 Index, OPEC).
 */
export type LabelClassification = "abbreviation" | "phrase" | "ric" | "stemmable";

export interface ResolvedLabel {
  name: string;
  primary?: boolean;
  category?: LabelCategory;
  classification?: LabelClassification | string;
  /** Per-label weight for text scanning; low means noisy as free text. */
  scanning_importance?: number;
}

export interface ResolvedTag {
  id: number;
  category?: TagCategory;
  labels?: ResolvedLabel[];
}

/**
 * Categories whose tags carry a tradeable symbol. These become ticker entries;
 * everything else is a topic.
 *
 * The split decides who gets a say: an instrument tag identifies the same
 * thing the user asked for, so it applies silently. A topic tag broadens what
 * matches — stories that never contain the word — so the user keeps a control
 * to drop it.
 *
 * `company` is deliberately NOT here. A company tag holds names, not symbols:
 * the Microsoft tag (160650) carries "Microsoft Corp" and "Microsoft", and the
 * symbol MSFT.US lives on a separate ticker tag. Companies reach their symbol
 * through the linked-ticker lookup instead, and are topics until they do.
 */
const SYMBOL_CATEGORIES: ReadonlySet<TagCategory> = new Set<TagCategory>([
  "ticker",
  "index",
  "fx_pair",
  "currency",
  "commodity",
]);

export type TermKind = "instrument" | "topic";

/** ISINs are `US0378331005` — two country letters, nine alnum, a check digit. */
const ISIN_PATTERN = /^[A-Z]{2}[A-Z0-9]{9}\d$/;

/**
 * The symbol-shaped label, if this tag has one at all.
 *
 * Reads the label metadata rather than the tag's category, because the two
 * disagree in practice: the Apple Inc *company* tag (202216) carries only
 * "Apple Inc", while the AAPL *ticker* tag (202217) carries AAPL / AAPL.OQ /
 * the ISIN. A company therefore has no symbol to store, and saying so here is
 * what stops "Apple Inc" being written into `tickers[]`.
 *
 * Preference: an explicit canonical/RIC label if the service ever sends
 * `category`, else the primary abbreviation, else the shortest abbreviation.
 * ISINs are excluded — they are abbreviations to the service but useless as a
 * match form.
 */
export function symbolLabel(tag: ResolvedTag): string | undefined {
  const labels = tag.labels ?? [];
  const isSymbol = (label: ResolvedLabel) =>
    !ISIN_PATTERN.test(label.name) && label.category !== "ISIN";
  const byCategory = (category: LabelCategory) =>
    labels.find((label) => label.category === category && isSymbol(label))?.name;

  const abbreviations = labels.filter(
    (label) => label.classification === "abbreviation" && isSymbol(label)
  );
  return (
    byCategory("canonical") ??
    abbreviations.find((label) => label.primary)?.name ??
    [...abbreviations].sort((a, b) => a.name.length - b.name.length)[0]?.name ??
    byCategory("RIC")
  );
}

/**
 * Instrument or topic.
 *
 * Category alone is not enough: `company` is in INSTRUMENT_CATEGORIES because a
 * company IS an instrument's issuer, but the company tag holds no symbol. A tag
 * is only an instrument if it can actually produce one, otherwise it is stored
 * as a topic — where its tag id still does the matching work.
 */
export function tagKind(tag: ResolvedTag): TermKind {
  return tag.category && SYMBOL_CATEGORIES.has(tag.category)
    ? "instrument"
    : "topic";
}

/**
 * The form to store for feeds that only carry a plain ticker string.
 *
 * The symbol if there is one, else the human name — so this stays safe to call
 * on any tag, while `tagKind` decides whether the result belongs in `tickers[]`.
 */
export function plainLabel(tag: ResolvedTag): string | undefined {
  const labels = tag.labels ?? [];
  return (
    symbolLabel(tag) ??
    labels.find((label) => label.category === "normal" && label.primary)?.name ??
    labels.find((label) => label.primary)?.name ??
    labels[0]?.name
  );
}

/** Human-facing name for a tag — the primary label, else the plain form. */
export function displayLabel(tag: ResolvedTag): string | undefined {
  return (tag.labels ?? []).find((label) => label.primary)?.name ?? plainLabel(tag);
}

/**
 * Whether a label is safe to match as free text.
 *
 * `scanning_importance` is the desk's own judgement on this — the answer to
 * the "Cook matches Fed's Cook AND Tim Cook" problem, from the people who
 * curate the taxonomy rather than from a heuristic here.
 */
export const MIN_SCANNING_IMPORTANCE = 0.5;

// Deliberately loose in its parameter: this reads one field, and is called
// with both `ResolvedLabel` and the rawer label shape off the search response.
export function isSafeToScan(label: {
  name?: string;
  scanning_importance?: number;
}): boolean {
  return (label.scanning_importance ?? 1) >= MIN_SCANNING_IMPORTANCE;
}

export interface ResolvedTerm {
  /** Exactly what the user typed, preserved for display and as a fallback. */
  input: string;
  kind: TermKind;
  /** Set when the term resolved to a tag. */
  tagId?: number;
  /** Plain form to store in tickers[] — instruments only. */
  ticker?: string;
  /** Term to store in keywords[] — topics and unresolved input. */
  keyword?: string;
  /** Tag offered as a broadening; the user can drop it. Topics only. */
  pairedTagLabel?: string;
  /**
   * Name of the tag the term matched, whatever its kind — "William Hill PLC"
   * for "William". Shown so a wrong match can be spotted: the stored ticker
   * alone (WMH.LN) doesn't tell anyone that a first name became a bookmaker.
   */
  tagLabel?: string;
}

/**
 * Decide what a typed term becomes.
 *
 * Kind comes from the lookup, never from the shape of the input: MAR, ALL,
 * KEY and ON are real tickers *and* ordinary words, "Apple" is a name rather
 * than a symbol, and "Fed" looks exactly like a ticker but is a topic. Casing
 * and length carry no signal.
 *
 * With no match the term becomes a keyword, which still matches text on every
 * feed — so a lookup failure degrades to today's behaviour instead of losing
 * what the user typed.
 */
export function resolveTerm(
  input: string,
  tag?: ResolvedTag,
  /**
   * Symbol recovered from a tag the search could not reach — see
   * `linkedTickerFor` in resolveTerms.ts. Typing "Apple" finds the *company*
   * tag, which holds no symbol; the ticker lives on a separate tag that only
   * answers to "AAPL". Passing it here is what turns that into a ticker entry.
   */
  linkedSymbol?: string
): ResolvedTerm {
  const trimmed = input.trim();
  if (!tag) return { input: trimmed, kind: "topic", keyword: trimmed };

  // The tag id stays the one the search found — the company — because that is
  // the tag the pipeline puts on headlines. Only the stored symbol comes from
  // the linked ticker, so text feeds and tagged feeds each get the form they
  // can match.
  if (linkedSymbol && tagKind(tag) !== "instrument") {
    return {
      input: trimmed,
      kind: "instrument",
      tagId: tag.id,
      ticker: linkedSymbol,
      tagLabel: displayLabel(tag),
    };
  }

  if (tagKind(tag) === "instrument") {
    // The tag identifies the instrument the user named, so it replaces the
    // typed form rather than widening it.
    return {
      input: trimmed,
      kind: "instrument",
      tagId: tag.id,
      ticker: plainLabel(tag) ?? trimmed.toUpperCase(),
      tagLabel: displayLabel(tag),
    };
  }

  return {
    input: trimmed,
    kind: "topic",
    tagId: tag.id,
    keyword: trimmed,
    pairedTagLabel: displayLabel(tag),
    tagLabel: displayLabel(tag),
  };
}
